"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import Button from "@/components/library/Button";
import CompanyCard from "@/components/hero/CompanyCard";
import type { CompanyInfo } from "@/app/api/company-lookup/route";
import { Icon } from "@/components/library/Icon";
import Image from "next/image";
import clsx from "clsx";
import styles from "@/components/Hero.module.css";
import { useTranslation } from "react-i18next";

function subscribeMobileQuery(callback: () => void) {
  const mq = globalThis.matchMedia("(max-width: 767px)");
  mq.addEventListener("change", callback);
  return () => mq.removeEventListener("change", callback);
}

function getMobileSnapshot() {
  return globalThis.matchMedia("(max-width: 767px)").matches;
}

function getMobileServerSnapshot() {
  return false;
}

function getScrollMaskImage(
  canLeft: boolean,
  canRight: boolean,
): string | undefined {
  if (canLeft && canRight) {
    return "linear-gradient(to right, transparent, black 24px, black calc(100% - 24px), transparent)";
  }
  if (canLeft) {
    return "linear-gradient(to right, transparent, black 24px)";
  }
  if (canRight) {
    return "linear-gradient(to left, transparent, black 24px)";
  }
  return undefined;
}

export type ShowcaseTab =
  | "ai-workflows"
  | "gail-for-you"
  | "ai-voice-calls"
  | "industry-expertise";

interface HeroShowcaseProps {
  activeTab: ShowcaseTab;
  setActiveTab: (tab: ShowcaseTab) => void;
  emailSubmitted: boolean;
  companyInfo: CompanyInfo | null;
  isLoading: boolean;
  domain: string | null;
}

const TAB_CONFIG: {
  key: ShowcaseTab;
  i18nKey: string;
  requiresEmail: boolean;
}[] = [
  {
    key: "ai-workflows",
    i18nKey: "hero.showcaseTabs.aiWorkflows",
    requiresEmail: false,
  },
  {
    key: "gail-for-you",
    i18nKey: "hero.showcaseTabs.gailForYou",
    requiresEmail: true,
  },
  {
    key: "ai-voice-calls",
    i18nKey: "hero.showcaseTabs.aiVoiceCalls",
    requiresEmail: false,
  },
  {
    key: "industry-expertise",
    i18nKey: "hero.showcaseTabs.industryExpertise",
    requiresEmail: false,
  },
];

type CarouselItem =
  | { key: ShowcaseTab; type: "image"; image: string }
  | { key: ShowcaseTab; type: "card" };

const ALL_CAROUSEL_ITEMS: CarouselItem[] = [
  {
    key: "ai-workflows",
    type: "image",
    image: "/images/hero-showcase/hero-workflows.webp",
  },
  { key: "gail-for-you", type: "card" },
  {
    key: "ai-voice-calls",
    type: "image",
    image: "/images/hero-showcase/hero-voice.webp",
  },
  {
    key: "industry-expertise",
    type: "image",
    image: "/images/hero-showcase/hero-experts.webp",
  },
];

type Slot = "center" | "right" | "left" | "hidden";

const DESKTOP_POSITION_STYLES: Record<
  Slot,
  { transform: string; opacity: number; zIndex: number; filter: string }
> = {
  center: {
    transform: "translateX(0%) scale(1)",
    opacity: 1,
    zIndex: 10,
    filter: "blur(0px)",
  },
  right: {
    transform: "translateX(60%) scale(0.85)",
    opacity: 0.4,
    zIndex: 5,
    filter: "blur(0.5px)",
  },
  left: {
    transform: "translateX(-60%) scale(0.85)",
    opacity: 0.4,
    zIndex: 5,
    filter: "blur(0.5px)",
  },
  hidden: {
    transform: "translateX(120%) scale(0.7)",
    opacity: 0,
    zIndex: 0,
    filter: "blur(2px)",
  },
};

const MOBILE_POSITION_STYLES: Record<
  Slot,
  { transform: string; opacity: number; zIndex: number; filter: string }
> = {
  center: {
    transform: "translateY(0%) scale(1)",
    opacity: 1,
    zIndex: 10,
    filter: "blur(0px)",
  },
  right: {
    transform: "translateY(-6%) scale(0.94)",
    opacity: 0.6,
    zIndex: 5,
    filter: "blur(0px)",
  },
  left: {
    transform: "translateY(-11%) scale(0.89)",
    opacity: 0.35,
    zIndex: 3,
    filter: "blur(0px)",
  },
  hidden: {
    transform: "translateY(-15%) scale(0.84)",
    opacity: 0,
    zIndex: 0,
    filter: "blur(2px)",
  },
};

const HeroShowcase = ({
  activeTab,
  setActiveTab,
  emailSubmitted,
  companyInfo,
  isLoading,
  domain,
}: HeroShowcaseProps) => {
  const { t } = useTranslation();
  const isMobile = useSyncExternalStore(
    subscribeMobileQuery,
    getMobileSnapshot,
    getMobileServerSnapshot,
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 2;
    setCanScrollLeft(el.scrollLeft > threshold);
    setCanScrollRight(
      el.scrollLeft + el.clientWidth < el.scrollWidth - threshold,
    );
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollFades();
    el.addEventListener("scroll", updateScrollFades, { passive: true });
    const ro = new ResizeObserver(updateScrollFades);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollFades);
      ro.disconnect();
    };
  }, [updateScrollFades]);

  const visibleTabs = TAB_CONFIG.filter(
    (tab) => !tab.requiresEmail || emailSubmitted,
  );

  const carouselItems = emailSubmitted
    ? ALL_CAROUSEL_ITEMS
    : ALL_CAROUSEL_ITEMS.filter((i) => i.key !== "gail-for-you");

  const activeIndex = Math.max(
    carouselItems.findIndex((i) => i.key === activeTab),
    0,
  );

  const getSlot = (itemIndex: number): Slot => {
    const n = carouselItems.length;
    const offset = (itemIndex - activeIndex + n) % n;
    if (offset === 0) return "center";
    if (offset === 1) return "right";
    if (offset === n - 1) return "left";
    return "hidden";
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const activeButton = container.querySelector<HTMLElement>(
      `[data-tab="${activeTab}"]`,
    );
    if (activeButton) {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      const scrollLeft =
        container.scrollLeft +
        (buttonRect.left - containerRect.left) -
        (containerRect.width - buttonRect.width) / 2;
      container.scrollTo({ left: scrollLeft, behavior: "smooth" });
    }
  }, [activeTab]);

  const handleTabClick = (key: ShowcaseTab) => {
    if (key === activeTab) return;
    setActiveTab(key);
  };

  const handlePrev = () => {
    const currentIdx = visibleTabs.findIndex((tab) => tab.key === activeTab);
    const prevIdx = (currentIdx - 1 + visibleTabs.length) % visibleTabs.length;
    setActiveTab(visibleTabs[prevIdx].key);
  };

  const handleNext = () => {
    const currentIdx = visibleTabs.findIndex((tab) => tab.key === activeTab);
    const nextIdx = (currentIdx + 1) % visibleTabs.length;
    setActiveTab(visibleTabs[nextIdx].key);
  };

  const positionStyles = isMobile
    ? MOBILE_POSITION_STYLES
    : DESKTOP_POSITION_STYLES;

  return (
    <div
      className={clsx(
        "w-full",
        emailSubmitted ? "mt-6 md:mt-8" : "mt-12 md:mt-16",
      )}
    >
      {/* Tab pills */}
      <div className="flex items-center justify-center md:gap-2.5 font-monument">
        {/* Left arrow – mobile only */}
        <button
          type="button"
          onClick={handlePrev}
          className="md:hidden shrink-0 w-8 h-8 flex items-center justify-center text-black/60 hover:text-black transition-colors"
          aria-label={t("hero.previousTab")}
        >
          <Icon icon="chevron-left" />
        </button>

        <div
          ref={scrollRef}
          className={clsx(
            "flex gap-2 md:gap-2.5 md:flex-wrap md:justify-center overflow-x-auto",
            styles.scrollableTabs,
          )}
          style={{
            maskImage: getScrollMaskImage(canScrollLeft, canScrollRight),
            WebkitMaskImage: getScrollMaskImage(canScrollLeft, canScrollRight),
          }}
        >
          {visibleTabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <span key={tab.key} data-tab={tab.key} className="shrink-0">
                <Button
                  type={isActive ? "primary" : "tertiary"}
                  onClick={() => handleTabClick(tab.key)}
                  compact
                  textClassName="text-xs! md:text-sm!"
                  className={clsx(
                    "font-medium transition-all duration-200 flex! items-center justify-center",
                    "px-3 py-1.5 md:px-5 md:py-2.5",
                    !isActive &&
                      "bg-transparent! hover:bg-black/8! border border-black/40",
                    isActive && "border border-transparent",
                  )}
                >
                  {t(tab.i18nKey)}
                </Button>
              </span>
            );
          })}
        </div>

        {/* Right arrow – mobile only */}
        <button
          type="button"
          onClick={handleNext}
          className="md:hidden shrink-0 w-8 h-8 flex items-center justify-center text-black/60 hover:text-black transition-colors"
          aria-label={t("hero.nextTab")}
        >
          <Icon icon="chevron-right" />
        </button>
      </div>

      {/* Carousel */}
      <div className="relative mx-auto max-w-7xl overflow-hidden">
        <div className="grid place-items-center pt-14 pb-6 md:pt-6 xl:pt-4 xl:pb-4">
          {carouselItems.map((item, index) => {
            const slot = getSlot(index);
            const posStyle = positionStyles[slot];
            const isCenter = slot === "center";
            const isHidden = slot === "hidden";

            return (
              <div
                key={item.key}
                className="col-start-1 row-start-1 w-[85%] sm:w-[65%] md:w-[55%] lg:w-[45%] max-w-[640px]"
                style={{
                  transform: posStyle.transform,
                  opacity: posStyle.opacity,
                  zIndex: posStyle.zIndex,
                  filter: posStyle.filter,
                  pointerEvents: isHidden ? "none" : undefined,
                  transition:
                    "transform 500ms ease-in-out, opacity 500ms ease-in-out, filter 500ms ease-in-out",
                }}
              >
                {item.type === "image" ? (
                  <button
                    type="button"
                    className={clsx(
                      "bg-white rounded-2xl shadow-xl overflow-hidden block w-full",
                      !isCenter && !isHidden && "cursor-pointer",
                      isCenter && "cursor-default",
                    )}
                    tabIndex={isCenter || isHidden ? -1 : 0}
                    onClick={() =>
                      !isCenter && !isHidden && handleTabClick(item.key)
                    }
                  >
                    <Image
                      src={item.image}
                      alt={t(
                        TAB_CONFIG.find((c) => c.key === item.key)?.i18nKey ||
                          "",
                      )}
                      width={1622}
                      height={1844}
                      className="w-full h-auto"
                    />
                  </button>
                ) : (
                  <div className="relative w-full text-left">
                    <CompanyCard
                      companyInfo={companyInfo}
                      isLoading={isLoading}
                      domain={domain}
                    />
                    {!isCenter && !isHidden && (
                      <button
                        type="button"
                        className="absolute inset-0 z-10 cursor-pointer appearance-none bg-transparent border-none"
                        onClick={() => handleTabClick(item.key)}
                        aria-label={t(
                          TAB_CONFIG.find((c) => c.key === item.key)?.i18nKey ||
                            "",
                        )}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HeroShowcase;
